import { advancedThresholdValuePropertyName } from "@/cache/CustomGeoJSONCache/utils";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import type { RetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import type { CustomGeoJsonRequestOption } from "@/layers/geojson/custom";
import type { AdvancedThreshold } from "@/overlay/components/CommonParts/Threshold/types";
import { addMultipleValuesToFeature } from "@/threads/DataProcessingThread/utils/custom-geojson";
import { Aggregate, type CoordinateSystem, type PolygonRequest } from "@mm/api.meteomatics.com";
import type { Feature, GeoJsonProperties, Geometry, Polygon } from "geojson";
import { type DateTime, Duration } from "luxon";

export function hydratePolygonFeaturesAdvancedThreshold(
  features: Array<Feature<Polygon, GeoJsonProperties>>,
  datetime: DateTime,
  customGeoJsonRequestOption: CustomGeoJsonRequestOption,
  retry: RetryState,
): Promise<Feature<Geometry, GeoJsonProperties>[]>[] {
  const tmpAdvancedThresholds: AdvancedThreshold[] = customGeoJsonRequestOption.advancedThresholds;

  return features.map((feature) => {
    const thresholdDetails = tmpAdvancedThresholds
      .flatMap((t) => t.details ?? [])
      .filter((detail) => detail.parameter && detail.model);
    const promises = thresholdDetails.map((detail) => {
      return apiThreadPool
        .getPolygonJson(
          createRequestPolygon(
            feature,
            detail.parameter,
            detail.model,
            datetime.plus(Duration.fromObject({ minutes: detail.offsetMinutes })),
            detail.aggregate,
          ),
          retry,
        )
        .then(({ data }) => {
          const value = data[0]?.coordinates[0]?.dates[0]?.value;
          return { [advancedThresholdValuePropertyName(detail.id)]: value };
        });
    });
    // Merge all values into a single feature once all promises resolve
    return Promise.allSettled(promises).then((results) => {
      const combinedValues = results.reduce(
        (acc, result) => {
          if (result.status === "fulfilled") {
            Object.assign(acc, result.value);
          }
          return acc;
        },
        {} as Record<string, number>,
      );

      return [addMultipleValuesToFeature(feature, combinedValues)];
    });
  });
}

// Helper function to create a PolygonRequest
function createRequestPolygon(
  feature: Feature<Polygon, GeoJsonProperties>,
  parameter: string,
  model: string,
  datetime: DateTime,
  aggregate?: Aggregate,
): PolygonRequest<CoordinateSystem.WGS84> {
  return {
    feature,
    aggregate: aggregate || Aggregate.max,
    startDatetime: datetime,
    duration: Duration.fromISO("PT1H"),
    temporalResolution: Duration.fromISO("PT1H"),
    width: 0,
    height: 0,
    parameters: [parameter],
    model,
    boundingBoxLimit: null,
  };
}
